import {defineStore} from 'pinia'
import {BoardsNs, FeatureNs, ScreenNs} from "@shared/types";
import {FigmaApi, ScreenApi} from "@/api";
import {useSelectedProjectStore} from "@domains/boards/stores/selected-project.store";
import {colors} from "@shared/constants/common.constants";
import {useFeatureStore} from "@domains/boards";
import ScreenTypeEnums = ScreenNs.ScreenTypeEnums;

const percentOf = (event: { loaded: number; total?: number }): number =>
  event.total ? Math.round((event.loaded / event.total) * 100) : 0

type Placed = Pick<ScreenNs.Screen, 'orderNumber' | 'screenType' | 'createDate'>

const bornLater = (a: Placed, b: Placed): number =>
  new Date(b.createDate ?? 0).getTime() - new Date(a.createDate ?? 0).getTime()

export const byBoardOrder = (a: Placed, b: Placed): number => {
  const arranged = (a.orderNumber ?? 0) - (b.orderNumber ?? 0)
  if (arranged !== 0) return arranged
  if (a.screenType !== ScreenTypeEnums.IMAGE || b.screenType !== ScreenTypeEnums.IMAGE) return 0
  return bornLater(a, b)
}


export const useScreenStore = defineStore("screen", {
  state: () => ({
    screenMap: new Map<string, ScreenNs.Screen>,
    screenTree: [] as ScreenNs.ScreenTreeNode[],
    selectedScreen: {} as ScreenNs.ScreenTreeNode,
    selectedNodeScreenList: [] as ScreenNs.Screen[],

    editingScreen: {} as ScreenNs.Screen,
    screenFeatures: [] as ScreenNs.ScreenFeatures[],
    isSelectModeOn: false,
    isSearchModeOn: false,
    isScreensCollapsed: false,
    ungroupedOnly: false
  }),
  getters: {
    getScreenList: (state) => [...state.screenMap.values()],
    getScreenTree: (state) => state.screenTree,
    getSelectedScreen: (state) => state.selectedScreen,
    getSelectedNodeScreenList: (state) => state.selectedNodeScreenList.sort(byBoardOrder),
    getEditingScreen: (state) => state.editingScreen,
    getScreenFeatures: (state) => state.screenFeatures,
    getNoSelectedScreenFeatures: (state) => state.screenFeatures.filter((feature: any) => !feature.box).reverse(),
    getSelectedScreenFeatures: (state) => state.screenFeatures.filter((feature: any) => feature.box)

  },
  actions: {
    convertFlatDataToTree() {
      const map = new Map<string, any>;

      this.screenMap.forEach((item: ScreenNs.Screen, key: string) => {
        map.set(item.id, {...item, ...item.properties, children: []} as ScreenNs.ScreenTreeNode);
      });

      const hierarchy: ScreenNs.ScreenTreeNode[] = [];
      this.screenMap.forEach((item: ScreenNs.Screen, key: string) => {
        const parent = item.parentId ? map.get(item.parentId) : undefined;
        if (parent) {
          parent.children.push(map.get(item.id));
        } else {
          hierarchy.push(map.get(item.id));
        }
      });

      hierarchy.forEach((item: any) => {
        if (item.screenType === ScreenNs.ScreenTypeEnums.GROUP) {
          item.children.sort(byBoardOrder)
        }
      });

      this.screenTree = hierarchy;
      this.screenTree.sort(byBoardOrder)
      return hierarchy;
    },

    async loadAllScreen(
        payload: ScreenNs.GetAllScreenRequest
    ): Promise<ScreenNs.Screen[]> {
      return ScreenApi.getAllScreen(payload)
      .then((screenList) => {
        this.setResultList(screenList);
        return [...this.screenMap.values()];
      })
    },

    setResultList(screenList: ScreenNs.Screen[]) {
      screenList.forEach(item => {
        if (item.screenType === ScreenTypeEnums.GROUP && (!item.properties?.color)) {
          item.properties ??= {};
          item.properties.color = colors[0];
        }
        this.screenMap.set(item.id, item);
      });
      this.convertFlatDataToTree();
    },

    async updateScreenParent(
        payload: ScreenNs.UpdateScreenParentRequest
    ): Promise<ScreenNs.Screen[]> {
      return ScreenApi.updateScreenParent(payload)
      .then((screenList) => {
        this.setResultList(screenList);
        return [...this.screenMap.values()];
      })
    },

    async updateScreenBoxes(payload: ScreenNs.UpdateScreenRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.updateScreenBoxes(payload)
      .then((response) => {
        const item = this.screenMap.get(payload.screen.id);
        if (item) {
          item.boxes = payload.screen.boxes;
        }
        return response;
      })
    },

    async showOrHideScreen(payload: ScreenNs.UpdateScreenRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.updateShowOrHide(payload)
      .then((response) => {
        const isHidden = payload.screen.properties.hidden;
        payload.screen.ids.forEach((id: string) => {
          const item = this.screenMap.get(id);
          if (item) {
            item.properties.hidden = isHidden;
          }
        });
        this.convertFlatDataToTree();
        return response;
      })
    },

    async deleteScreen(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.deleteScreen(payload)
      .then((response) => {
        const gone = new Set<string>(payload.screen.ids.map(String));
        let sweeping = true;
        while (sweeping) {
          sweeping = false;
          this.screenMap.forEach((item: ScreenNs.Screen) => {
            if (item.parentId && gone.has(item.parentId) && !gone.has(item.id)) {
              gone.add(item.id);
              sweeping = true;
            }
          });
        }
        gone.forEach((id) => this.screenMap.delete(id));
        this.convertFlatDataToTree();
        return response;
      })
    },

    // The board, looked at through its tail.
    selectUngrouped() {
      this.selectAScreen(undefined);
      this.ungroupedOnly = true;
      this.refreshSelectedNodeScreenList();
    },

    getScreenById(screenId: string): ScreenNs.Screen | undefined {
      return this.screenMap.get(screenId);
    },

    selectAScreenById(screenId: string, callback = () => {
    }) {
      const findScreen = this.screenTree.find((item) => item.id == screenId)
      if (findScreen) {
        this.selectAScreen(findScreen, callback);
      } else {
        this.selectAScreen(undefined, callback);
      }
    },

    selectAScreen(selectedItem: ScreenNs.ScreenTreeNode | undefined, callback = () => {
    }) {
      const editorStore = useSelectedProjectStore();
      this.ungroupedOnly = false;

      let theScreen: ScreenNs.ScreenTreeNode | undefined = selectedItem;
      if (!theScreen || theScreen.screenType == ScreenNs.ScreenTypeEnums.PROJECT) {
        theScreen = {
          id: undefined,
          isIndex: true,
          summary: editorStore.selectedProject.name,
          children: this.screenTree,
          screenType: ScreenNs.ScreenTypeEnums.PROJECT
        } as any;
      }

      if (this.selectedScreen !== theScreen) {
        const screenId = theScreen!.id;
        if (screenId) {
          this.router.push({
            name: BoardsNs.BoardsRouteEnums.SCREEN_SELECTED,
            params: {screenId}
          });
        } else {
          this.router.push({
            name: BoardsNs.BoardsRouteEnums.SCREEN
          });
        }

        this.selectedScreen = theScreen!;
        if (this.selectedScreen.screenType !== 'PROJECT') {
          const editorStore = useSelectedProjectStore()
          editorStore.addItemToRecentlyViewed({type: 'SCREEN', item: this.selectedScreen});
        }

        this.refreshSelectedNodeScreenList();
        callback();
      }
    },

    refreshSelectedScreen() {
      if (this.selectedScreen) {
        if (this.selectedScreen.screenType == ScreenNs.ScreenTypeEnums.PROJECT) {
          this.selectedScreen.children = this.screenTree;
        } else if (this.selectedScreen.screenType == ScreenNs.ScreenTypeEnums.GROUP) {
          const findScreen = this.screenTree.find((item) => item.id == this.selectedScreen.id)
          if (findScreen) {
            this.selectedScreen = findScreen;
          } else {
            this.selectAScreen(undefined);
          }
        }
        this.refreshSelectedNodeScreenList();
      }
    },

    refreshSelectedNodeScreenList() {
      if (this.selectedScreen) {
        if (this.selectedScreen.screenType !== 'PROJECT') {
          this.selectedNodeScreenList = [this.selectedScreen]
        } else {
          const newList: any[] = [];
          const screenWithoutGroup: any[] = [];
          this.selectedScreen.children.forEach((item: any) => {
            if (item.screenType == 'GROUP') {
              newList.push(item);
            } else {
              screenWithoutGroup.push(item);
            }
          })

          const bucket = {
            screenType: ScreenNs.ScreenTypeEnums.GROUP,
            children: screenWithoutGroup
          }
          newList.push(bucket)

          this.selectedNodeScreenList = this.ungroupedOnly ? [bucket] as never : newList
        }
      }
    },


    async renameScreen(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.renameScreen(payload)
      .then((response) => {
        const screen = this.screenMap.get(payload.screen.ids[0]);
        if (screen) {
          screen.summary = payload.screen.summary;
        }
        this.convertFlatDataToTree();
        return response
      })
    },

    async patchScreen(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.updateScreen(payload)
      .then((response) => {
        const screen = this.screenMap.get(payload.screen.ids?.[0] ?? payload.screen.id);
        if (screen) {
          const {ids: _ids, id: _id, ...fields} = payload.screen;
          Object.assign(screen, fields);
        }
        this.convertFlatDataToTree();
        return response
      })
    },

    async duplicateScreen(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.duplicateScreen(payload)
      .then((response) => response)
    },

    async changeScreenColor(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.changeScreenColor(payload)
      .then((response) => {
        const screen = this.screenMap.get(payload.screen.ids[0]);
        if (screen) {
          screen.properties.color = payload.screen.properties.color;
        }
        this.convertFlatDataToTree();
        return response
      })
    },

    async createAGroup(
        payload: ScreenNs.UpdateScreenRequest
    ): Promise<ScreenNs.Screen> {
      return ScreenApi.createAGroup(payload)
      .then((createdGroup) => createdGroup)
    },

    async createScreenWithoutImage(workspaceId: string, projectId: string, parentId: string = '', hidden: boolean = false): Promise<ScreenNs.Screen> {
      const payload = {
        workspaceId: workspaceId,
        projectId: projectId,
        screenType: ScreenNs.ScreenTypeEnums.IMAGE,
        parentId,
        summary: "Untitled Screen",
        hidden
      } as any;
      return ScreenApi.createScreenWithoutImage(payload)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        this.refreshSelectedScreen();
        return screen;
      })
    },

    async createScreenPlaceholder(workspaceId: string, projectId: string, summaryList: string[], relatedFeatureId: string, createNewGroup: boolean = false): Promise<ScreenNs.Screen[]> {
      const payload = {
        workspaceId: workspaceId,
        projectId: projectId,
        screenType: ScreenNs.ScreenTypeEnums.IMAGE,
        summaryList,
        relatedFeatureId,
        createNewGroup
      } as any;
      return ScreenApi.createScreenPlaceholder(payload)
      .then((screens: ScreenNs.Screen[]) => {
        this.setResultList(screens)
        this.refreshSelectedScreen();
        return screens;
      })
    },

    async uploadScreenImage(payload: FormData, onProgress?: (percent: number) => void): Promise<ScreenNs.Screen> {
      return ScreenApi.uploadScreen(payload, onProgress ? { onUploadProgress: (e) => onProgress(percentOf(e)) } : undefined)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        return screen;
      })
    },

    async replaceScreenImage(payload: FormData, onProgress?: (percent: number) => void): Promise<ScreenNs.Screen> {
      return ScreenApi.replaceScreen(payload, onProgress ? { onUploadProgress: (e) => onProgress(percentOf(e)) } : undefined)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        this.refreshSelectedScreen();
        return screen;
      })
    },

    startEditingScreen(selectedItem: ScreenNs.ScreenTreeNode) {
      this.editingScreen = this.screenMap.get(selectedItem.id)!;
    },

    loadScreenFeatures(screen: ScreenNs.Screen): void {
      if (screen.featureList && screen.featureList.length > 0) {
        const featureStore = useFeatureStore();
        let screenFeatures: any = featureStore.getFeatureList.filter((f: FeatureNs.Feature) => screen.featureList.includes(f.id));
        if (screen.boxes?.length >= 0) {
          screenFeatures = screenFeatures.map((feature: any) => ({
            ...feature,
            box: screen.boxes.find((box: any) => box.featureId === feature.id)
          }));
        }
        this.screenFeatures = screenFeatures;
      } else {
        this.screenFeatures = []
      }
    },

    async addScreenFeatures(payload: ScreenNs.ScreenFeatureRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.addScreenFeatures(payload)
      .then((response: ScreenNs.Screen) => {
        this.screenMap.set(response.id, response);
        return response;
      })
    },

    async updateScreenFeatures(payload: ScreenNs.ScreenFeatureRequest): Promise<any> {
      return ScreenApi.updateScreenFeatures(payload)
      .then(() => true)
    },

    async removeScreenFeatures(payload: ScreenNs.ScreenFeatureRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.removeScreenFeatures(payload)
      .then((response: ScreenNs.Screen) => {
        this.screenMap.set(response.id, response);
        return response;
      })
    },

    setSelectMode(isSelectModeOn: boolean): void {
      this.isSelectModeOn = isSelectModeOn;
    },

    setSearchMode(isSearchModeOn: boolean): void {
      this.isSearchModeOn = isSearchModeOn;
    },

    switchScreensCollapsedValue(): void {
      this.isScreensCollapsed = !this.isScreensCollapsed;
    },

    exportScreenTree(): string {
      return this.convertScreenTreeToFlat(this.screenTree, '').join('\n');
    },

    convertScreenTreeToFlat(tree: ScreenNs.ScreenTreeNode[], prefix = ''): string[] {
      return tree.flatMap((node, index) => {
        const currentPrefix = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        const lines: string[] = [];

        if (node.screenType === 'GROUP') {
          lines.push('');
        }
        lines.push(`${currentPrefix} ${node.summary}`);

        const childrenLines = node.children
            ? this.convertScreenTreeToFlat(node.children as ScreenNs.ScreenTreeNode[], currentPrefix)
            : [];

        return [...lines, ...childrenLines];
      });
    },

    async setCurrentVersion(payload: ScreenNs.SetCurrentVersionRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.setCurrentVersion(payload)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        this.refreshSelectedScreen();
        return screen;
      })
    },

    async deleteVersion(payload: ScreenNs.DeleteVersionRequest): Promise<ScreenNs.Screen> {
      return ScreenApi.deleteVersion(payload)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        this.refreshSelectedScreen();
        return screen;
      })
    },

    async updateScreenFromFigma(payload: ScreenNs.UpdateScreenFromFigmaRequest): Promise<ScreenNs.Screen> {
      return FigmaApi.updateScreenFromFigma(payload)
      .then((screen: ScreenNs.Screen) => {
        this.setResultList([screen])
        this.refreshSelectedScreen();
        return screen;
      })
    },

    async getFigmaProjectName(payload: ScreenNs.GetFigmaProjectNameRequest): Promise<string> {
      return FigmaApi.getFigmaProjectName(payload)
      .then((response: ScreenNs.FigmaProjectNameResponse) => response.projectName)
    }

  }
})
